import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '@/api/client';

// ===================================
// Brand Context - Global Brand State
// ===================================

interface Brand {
  id: string;
  name: string;
  industry: string;
  description?: string;
  website?: string;
  status: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  settings?: any;
  integrations?: any;
  createdAt: string;
  updatedAt: string;
  _count?: {
    campaigns: number;
    teamMembers: number;
  };
  // Research status from Story 14.10/14.11
  researchStatus?: 'pending' | 'in_progress' | 'completed' | 'failed';
}

interface BrandContextType {
  selectedBrand: Brand | null;
  currentBrand: Brand | null; // alias for selectedBrand
  brands: Brand[];
  isLoading: boolean;
  error: string | null;
  selectBrand: (brandId: string | null) => void;
  refreshBrands: () => Promise<void>;
  clearBrandSelection: () => void;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

interface BrandProviderProps {
  children: ReactNode;
}

export function BrandProvider({ children }: BrandProviderProps) {
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  // Load brands on mount
  useEffect(() => {
    loadBrands();
  }, []);

  // Load selected brand from localStorage on mount
  useEffect(() => {
    const savedBrandId = localStorage.getItem('selectedBrandId');
    if (savedBrandId && brands.length > 0) {
      const brand = brands.find(b => b.id === savedBrandId);
      if (brand) {
        setSelectedBrand(brand);
        applyBrandTheme(brand);
      }
    }
  }, [brands]);

  // Sync brand selection when route changes
  // This ensures the brand selector reflects the current URL
  useEffect(() => {
    if (brands.length === 0) return;

    // Extract brandId from current route
    // Routes: /brands/:id, /dashboard/:brandId, /campaigns, etc.
    const pathname = location.pathname;
    let brandIdFromRoute: string | null = null;

    // Check if we're on /brands/:id route
    const brandsMatch = pathname.match(/^\/brands\/([^/]+)/);
    if (brandsMatch) {
      brandIdFromRoute = brandsMatch[1];
    }

    // Check if we're on /dashboard/:brandId route
    const dashboardMatch = pathname.match(/^\/dashboard\/([^/]+)/);
    if (dashboardMatch) {
      brandIdFromRoute = dashboardMatch[1];
    }

    // If we found a brand ID in the route, sync it with the context
    if (brandIdFromRoute) {
      const brand = brands.find(b => b.id === brandIdFromRoute);
      if (brand && selectedBrand?.id !== brandIdFromRoute) {
        setSelectedBrand(brand);
        localStorage.setItem('selectedBrandId', brandIdFromRoute);
        applyBrandTheme(brand);
      }
    }
  }, [location.pathname, brands]);

  const loadBrands = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get('/brands');

      if (response.data.success) {
        // Deduplicate brands by name to handle duplicates from backend
        // Keep the most recently created version of each brand name
        const sortedBrands = (response.data.data as Brand[]).sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        const uniqueBrands = [...new Map(
          sortedBrands.map((brand: Brand) => [brand.name, brand])
        ).values()];
        setBrands(uniqueBrands);
      }
    } catch (err: any) {
      console.error('Error loading brands:', err);
      setError(err.message || 'Failed to load brands');
    } finally {
      setIsLoading(false);
    }
  };

  const selectBrand = (brandId: string | null) => {
    if (!brandId) {
      clearBrandSelection();
      return;
    }

    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      setSelectedBrand(brand);
      localStorage.setItem('selectedBrandId', brandId);
      applyBrandTheme(brand);
    }
  };

  const clearBrandSelection = () => {
    setSelectedBrand(null);
    localStorage.removeItem('selectedBrandId');
    removeBrandTheme();
  };

  const refreshBrands = async () => {
    await loadBrands();
  };

  // Apply brand theme colors to the document
  const applyBrandTheme = (brand: Brand) => {
    const root = document.documentElement;

    // Apply CSS custom properties for brand colors
    root.style.setProperty('--brand-primary', brand.primaryColor);
    root.style.setProperty('--brand-secondary', brand.secondaryColor);

    // Add brand-themed class to body
    document.body.classList.add('brand-themed');
    document.body.setAttribute('data-brand-id', brand.id);
  };

  // Remove brand theme
  const removeBrandTheme = () => {
    const root = document.documentElement;
    root.style.removeProperty('--brand-primary');
    root.style.removeProperty('--brand-secondary');

    document.body.classList.remove('brand-themed');
    document.body.removeAttribute('data-brand-id');
  };

  const value: BrandContextType = {
    selectedBrand,
    currentBrand: selectedBrand, // alias for selectedBrand
    brands,
    isLoading,
    error,
    selectBrand,
    refreshBrands,
    clearBrandSelection
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}

// Export types
export type { Brand, BrandContextType };
